/**
 * Tabs der Bereichsseite (D-00c, BE-03) - rein und getestet.
 *
 * Jeder Bereich hat den Tab "Überblick" (das Board). "Gesundheit & Körper"
 * fasst Training und Ernährung zusammen: ihre bisherigen Adressen bleiben und
 * stehen jetzt als Tabs im Rahmen des Bereichs, dazu Laufen und Körperdaten.
 * "Schule" hat Stundenplan, Fächer, Noten, Klausuren und Mitschriften.
 * "Finanzen" hat Buchungen, Budget und Abos & Fristen Sparziele, Abos & Fristen und Auswertung.
 */
import type { Area } from '@/domain/types';

export interface BereichsTab {
  key: string;
  label: string;
  to: string;
  /** Ob der Tab bei diesem Pfad aktiv ist (der laengste passende gewinnt in `aktiverTab`). */
  praefix: string;
  /** Weitere Pfade, bei denen der Tab aktiv ist (z. B. die Fachseiten unter "Fächer"). */
  weitere?: string[];
}

export function bereichsTabs(area: Pick<Area, 'id' | 'fest'>): BereichsTab[] {
  const ueberblick: BereichsTab = { key: 'ueberblick', label: 'Überblick', to: `/bereiche/${encodeURIComponent(area.id)}`, praefix: `/bereiche/${encodeURIComponent(area.id)}` };
  if (area.fest === 'schule') {
    return [
      ueberblick,
      { key: 'stundenplan', label: 'Stundenplan', to: '/schule/stundenplan', praefix: '/schule/stundenplan' },
      { key: 'faecher', label: 'Fächer', to: '/schule/faecher', praefix: '/schule/faecher', weitere: ['/schule/fach'] },
      { key: 'noten', label: 'Noten', to: '/schule/noten', praefix: '/schule/noten' },
      { key: 'klausuren', label: 'Klausuren & Lernpläne', to: '/schule/klausuren', praefix: '/schule/klausuren' },
      { key: 'mitschriften', label: 'Mitschriften', to: '/schule/mitschriften', praefix: '/schule/mitschriften' },
    ];
  }
  if (area.fest === 'finanzen') {
    return [
      ueberblick,
      { key: 'buchungen', label: 'Buchungen', to: '/finanzen/buchungen', praefix: '/finanzen/buchungen' },
      { key: 'budget', label: 'Budget', to: '/finanzen/budget', praefix: '/finanzen/budget' },
      { key: 'sparziele', label: 'Sparziele', to: '/finanzen/sparziele', praefix: '/finanzen/sparziele' },
      { key: 'abos', label: 'Abos & Fristen', to: '/finanzen/abos', praefix: '/finanzen/abos' },
      { key: 'auswertung', label: 'Auswertung', to: '/finanzen/auswertung', praefix: '/finanzen/auswertung' },
    ];
  }
  if (area.fest !== 'gesundheit') return [ueberblick];
  return [
    ueberblick,
    { key: 'training', label: 'Training', to: '/training', praefix: '/training' },
    { key: 'laufen', label: 'Laufen', to: '/training/laufen', praefix: '/training/laufen' },
    { key: 'ernaehrung', label: 'Ernährung', to: '/ernaehrung', praefix: '/ernaehrung' },
    { key: 'koerperdaten', label: 'Körperdaten', to: '/training/koerperdaten', praefix: '/training/koerperdaten' },
    { key: 'uebungen', label: 'Übungen', to: '/training/uebungen', praefix: '/training/uebungen' },
  ];
}

/** Der aktive Tab zu einem Pfad: der mit dem laengsten passenden Praefix. */
export function aktiverTab(tabs: readonly BereichsTab[], pfad: string): string | undefined {
  const passt = (p: string) => pfad === p || pfad.startsWith(`${p}/`);
  const treffer = tabs.flatMap((t) => [t.praefix, ...(t.weitere ?? [])].filter(passt).map((p) => ({ key: t.key, laenge: p.length })));
  return treffer.sort((a, b) => b.laenge - a.laenge)[0]?.key;
}
